export interface GradientColors {
    start: string;
    end?: string;
    subtitle: string;
}

export interface Segment {
    id: string;
    label: string;
    value: number;
    valueOverride?: string;
    colors: GradientColors;
    subtitle?: React.ReactNode;
    tooltip?: React.ReactNode;
    subsegments?: Subsegment[];
}

export type Subsegment = Omit<Segment, "subsegments" | "colors">;

export const calculateWidthPercentage = (
    segment: Subsegment,
    segments: Subsegment[],
): number => {
    const { value } = segment;
    const total = segments.reduce((acc, s) => acc + s.value, 0);
    return Number(((value / total) * 100).toFixed(1));
};

interface CalculateIntermediateColorParams {
    startColor: string;
    endColor?: string;
    step: number;
    totalSteps: number;
}

const calculateIntermediateColor = ({
    startColor,
    endColor,
    step,
    totalSteps,
}: CalculateIntermediateColorParams): string => {
    if (!endColor) {
        return startColor;
    }

    const startRGB = startColor.match(/\w\w/g)?.map((x) => parseInt(x, 16)) ?? [
        0, 0, 0,
    ];
    const endRGB = endColor.match(/\w\w/g)?.map((x) => parseInt(x, 16)) ?? [
        0, 0, 0,
    ];

    const r = Math.round(
        startRGB[0] + (endRGB[0] - startRGB[0]) * (step / totalSteps),
    );
    const g = Math.round(
        startRGB[1] + (endRGB[1] - startRGB[1]) * (step / totalSteps),
    );
    const b = Math.round(
        startRGB[2] + (endRGB[2] - startRGB[2]) * (step / totalSteps),
    );

    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
};

export const getSubsegmentStyle = (
    segment: Segment,
    subsegment: Subsegment,
    index: number,
) => {
    if (segment.colors) {
        const totalSubsegments = segment.subsegments?.length ?? 1;
        const minSteps = 4;
        const totalSteps =
            totalSubsegments < minSteps ? minSteps : totalSubsegments;
        const step = index;

        const backgroundColor = calculateIntermediateColor({
            startColor: segment.colors.start,
            endColor: segment.colors.end,
            step,
            totalSteps,
        });
        return {
            width:
                calculateWidthPercentage(
                    subsegment,
                    segment.subsegments ?? [],
                ) + "%",
            backgroundColor,
        };
    }
    return {
        width:
            calculateWidthPercentage(subsegment, segment.subsegments ?? []) +
            "%",
    };
};
